class EasyOrderSection extends HTMLElement {
  constructor() {
    super();
    this.searchInput = null;
    this.results = null;
    this.status = null;
    this.initialResults = '';
    this.debounceTimer = 0;
    this.abortController = null;
  }

  connectedCallback() {
    this.searchInput = this.querySelector('[data-easy-order-search-input]');
    this.results = this.querySelector('[data-easy-order-results]');
    this.status = this.querySelector('[ref="status"]');
    this.initialResults = this.results?.innerHTML || '';

    this.searchInput?.addEventListener('input', this.onSearchInput);
    this.results?.addEventListener('click', this.onResultsClick);
  }

  disconnectedCallback() {
    this.searchInput?.removeEventListener('input', this.onSearchInput);
    this.results?.removeEventListener('click', this.onResultsClick);
    this.abortController?.abort();
    window.clearTimeout(this.debounceTimer);
  }

  onSearchInput = () => {
    window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => {
      this.search(this.searchInput?.value.trim() || '');
    }, 450);
  };

  onResultsClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.pagination a, [data-easy-order-ajax-page]');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    const url = new URL(link.href);
    url.searchParams.delete('view');
    url.searchParams.set('section_id', this.dataset.resultsSectionId || 'easy-order-results');
    this.fetchResults(url.toString());
  };

  async search(query) {
    if (!query) {
      await this.loadCollectionResults();
      return;
    }

    const searchUrl = this.dataset.searchUrl || '/search';
    const predictiveSearchUrl = this.dataset.predictiveSearchUrl || '/search/suggest';
    const url = new URL(predictiveSearchUrl || searchUrl, window.location.origin);
    const predictiveQuery = [...this.getSelectedFilterLabels(), query].join(' ').trim();

    url.searchParams.set('section_id', 'easy-order-predictive-results');
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '50');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('q', predictiveQuery);

    await this.fetchResults(url.toString());
  }

  async loadCollectionResults() {
    const collectionUrl = this.dataset.collectionUrl || window.location.pathname;
    const url = new URL(collectionUrl, window.location.origin);
    const currentUrl = new URL(window.location.href);

    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (key.startsWith('filter.') || key === 'sort_by') url.searchParams.append(key, value);
    }

    url.searchParams.set('section_id', this.dataset.resultsSectionId || 'easy-order-results');
    await this.fetchResults(url.toString(), { sortByPosition: false });
  }

  getSelectedFilterLabels() {
    const labels = Array.from(this.querySelectorAll('.facets input[name^="filter."]:checked'))
      .map((input) => {
        if (!(input instanceof HTMLInputElement)) return '';
        if (input.dataset.label) return input.dataset.label;
        if (input.getAttribute('aria-label')) return input.getAttribute('aria-label') || '';
        const label = input.closest('label') || this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        return label?.textContent?.trim().replace(/\s+/g, ' ') || '';
      })
      .filter(Boolean);

    return [...new Set(labels)];
  }

  async fetchResults(url, options = {}) {
    if (!this.results) return;

    const { sortByPosition = true } = options;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.classList.add('is-loading');
    this.setStatus('Loading products...');

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      this.results.innerHTML = await response.text();
      if (sortByPosition) this.sortRowsByPosition();
      this.setStatus('');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.setStatus('Unable to load products. Please try again.');
      console.error(error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  setStatus(message) {
    if (!this.status) return;
    this.status.textContent = message;
  }

  sortRowsByPosition() {
    const rowsContainer = this.results?.querySelector('.easy-order__rows');
    if (!rowsContainer) return;

    const rows = Array.from(rowsContainer.querySelectorAll('.easy-order__row'));
    rows
      .sort((a, b) => {
        const positionA = Number.parseInt(a.dataset.easyOrderPosition || '', 10);
        const positionB = Number.parseInt(b.dataset.easyOrderPosition || '', 10);
        const hasPositionA = Number.isFinite(positionA);
        const hasPositionB = Number.isFinite(positionB);

        if (hasPositionA && hasPositionB) return positionA - positionB;
        if (hasPositionA) return -1;
        if (hasPositionB) return 1;
        return 0;
      })
      .forEach((row) => rowsContainer.append(row));
  }
}

if (!customElements.get('easy-order-section')) {
  customElements.define('easy-order-section', EasyOrderSection);
}
